// Flappy Bird leaderboards: global top-10 + monthly top-10.
//
// Source of truth is the local SQLite store on the auth service (table
// `flappy_scores` in the analytics db). The browser cannot touch SQLite
// directly, so every call below goes through the same-origin API that Caddy
// proxies to that service:
//
//   GET  /api/session        -> { kind: 'user' | 'guest', name: string | null }
//   POST /api/session/guest  -> mints the temporary guest label when needed
//   POST /api/flappy/scores  -> { score, durationMs } (identity from cookie)
//   GET  /api/flappy/scores  -> { month, global: Entry[], monthly: Entry[] }
//
// Identity rule (enforced server-side, mirrored here for display): a signed-in
// user plays under their claimed username, everyone else under the temporary
// guest label the service gave them. The name is never taken from user input.
//
// When the API is unreachable (dev server, offline), scores fall back to a
// localStorage journal so the boards still work. The next successful submit
// heals the server board; the local journal is never uploaded in bulk.

export type IdentityKind = 'user' | 'guest';

export interface PlayerIdentity {
  kind: IdentityKind;
  name: string | null;
  needsUsername?: boolean;
}

export interface ScoreEntry {
  name: string;
  kind: string;
  score: number;
  ts?: number;
  day?: string;
}

export interface Leaderboards {
  month: string;
  global: ScoreEntry[];
  monthly: ScoreEntry[];
}

export const EMPTY_BOARDS: Leaderboards = { month: monthKeyLocal(Date.now()), global: [], monthly: [] };

const LOCAL_SCORES_KEY = 'flappy-bird-local-scores';
const LOCAL_NAME_KEY = 'flappy-bird-temp-name';
const MAX_LOCAL_ROWS = 200;

/** UTC day key, e.g. 2026-09-28. Matches the server's dayKey. */
export function dayKeyLocal(at: number | Date = Date.now()): string {
  return new Date(at).toISOString().slice(0, 10);
}

/** UTC month key, e.g. 2026-09. Matches the server's monthKey. */
export function monthKeyLocal(at: number | Date = Date.now()): string {
  return new Date(at).toISOString().slice(0, 7);
}

/** Short display label for a YYYY-MM month key, e.g. "Sep 2026". */
export function monthLabel(month: string): string {
  const d = new Date(`${month}-01T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return 'Monthly top 10';
  const label = d.toLocaleString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
  return `${label} top 10`;
}

async function getJson<T>(path: string, init?: RequestInit): Promise<T | null> {
  try {
    const response = await fetch(`/api${path}`, { credentials: 'same-origin', ...init });
    if (!response.ok) return null;
    return (await response.json()) as T;
  } catch {
    return null;
  }
}

/**
 * Resolves who is playing. Signed-in users come back with their claimed
 * username; everyone else gets the temporary guest label (minted here on
 * first visit). Returns null only when the auth service is unreachable.
 */
export async function ensureIdentity(): Promise<PlayerIdentity | null> {
  const existing = await getJson<PlayerIdentity>('/session');
  if (existing && existing.name) return existing;
  if (existing && existing.needsUsername) return existing;
  if (existing && existing.kind === 'guest') {
    // Guest session without a name should not happen, but fall through to
    // minting rather than stranding the player.
  }
  const guest = await getJson<PlayerIdentity>('/session/guest', { method: 'POST' });
  if (guest) return guest;
  return existing ?? null;
}

/** Public boards. Null when the service is unreachable (caller uses local). */
export async function fetchLeaderboards(): Promise<Leaderboards | null> {
  const boards = await getJson<
    Leaderboards & { day?: string; daily?: ScoreEntry[] }
  >('/flappy/scores');
  if (!boards || !Array.isArray(boards.global)) return null;
  // Tolerate the pre-monthly shape ({ day, daily }) during rollout.
  const monthly = Array.isArray(boards.monthly) ? boards.monthly : boards.daily;
  if (!Array.isArray(monthly)) return null;
  const month =
    typeof boards.month === 'string'
      ? boards.month
      : typeof boards.day === 'string'
        ? boards.day.slice(0, 7)
        : monthKeyLocal(Date.now());
  return {
    month,
    global: boards.global.slice(0, 10),
    monthly: monthly.slice(0, 10),
  };
}

export interface SubmitResult {
  ok: boolean;
  name?: string;
  kind?: string;
  leaderboards?: Leaderboards | null;
  error?: 'no_session' | 'needs_username' | 'unavailable' | 'invalid_score';
}

/**
 * Submits one finished run. The server reads identity from the session
 * cookie, so the body carries only the score. Never throws; reports the
 * failure reason for the UI to explain.
 */
export async function submitScore(score: number, durationMs?: number): Promise<SubmitResult> {
  if (!Number.isFinite(score) || score <= 0) return { ok: false, error: 'invalid_score' };
  try {
    const response = await fetch('/api/flappy/scores', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ score: Math.round(score), durationMs: Math.round(durationMs ?? 0) }),
    });
    if (response.status === 401) return { ok: false, error: 'no_session' };
    const body = (await response.json().catch(() => null)) as {
      ok?: boolean;
      name?: string;
      kind?: string;
      leaderboards?: Leaderboards;
      error?: string;
    } | null;
    if (!response.ok) {
      if (body?.error === 'needs_username') return { ok: false, error: 'needs_username' };
      return { ok: false, error: 'unavailable' };
    }
    return {
      ok: true,
      name: body?.name,
      kind: body?.kind,
      leaderboards: body?.leaderboards ?? null,
    };
  } catch {
    return { ok: false, error: 'unavailable' };
  }
}

// ── Local fallback (auth unreachable) ────────────────────────────────────────

function readLocal(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocal(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage full or blocked — boards just won't persist */
  }
}

export function loadLocalScores(): ScoreEntry[] {
  try {
    const raw = readLocal(LOCAL_SCORES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ScoreEntry[];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (r) =>
        r && typeof r.name === 'string' && Number.isFinite(r.score) && (r.score as number) >= 0,
    );
  } catch {
    return [];
  }
}

/** Stable temporary name for offline play, so the local board has an owner. */
export function getOrCreateLocalName(): string {
  const existing = readLocal(LOCAL_NAME_KEY);
  if (existing && existing.trim().length >= 3) return existing;
  const name = `Guest#${String(Math.floor(1000 + Math.random() * 9000))}`;
  writeLocal(LOCAL_NAME_KEY, name);
  return name;
}

export function recordLocalScore(name: string, kind: string, score: number, at = Date.now()): ScoreEntry[] {
  const rows = loadLocalScores();
  rows.push({ name, kind, score, ts: at, day: dayKeyLocal(at) });
  rows.sort((a, b) => (b.ts ?? 0) - (a.ts ?? 0));
  const trimmed = rows.slice(0, MAX_LOCAL_ROWS);
  writeLocal(LOCAL_SCORES_KEY, JSON.stringify(trimmed));
  return trimmed;
}

/** Best score per display name (case-insensitive), highest first. */
export function bestPerPlayer(rows: ScoreEntry[], limit = 10): ScoreEntry[] {
  const best = new Map<string, ScoreEntry>();
  for (const row of rows) {
    const key = row.name.toLowerCase();
    const prev = best.get(key);
    if (!prev || row.score > prev.score || (row.score === prev.score && (row.ts ?? 0) < (prev.ts ?? 0))) {
      best.set(key, row);
    }
  }
  return [...best.values()].sort((a, b) => b.score - a.score || (a.ts ?? 0) - (b.ts ?? 0)).slice(0, limit);
}

export function localLeaderboards(month = monthKeyLocal(Date.now())): Leaderboards {
  const rows = loadLocalScores();
  return {
    month,
    global: bestPerPlayer(rows, 10),
    monthly: bestPerPlayer(
      rows.filter((r) => (r.day ?? dayKeyLocal(r.ts ?? Date.now())).slice(0, 7) === month),
      10,
    ),
  };
}
